/**
 * Token-based API client for cross-domain authentication
 * Sends APS token in headers instead of relying on cookies
 */

import { apiRequest } from './queryClient';

/**
 * Get the current user's APS token from auth state
 */
function getApsToken(): string | null {
  // Try to get token from localStorage (stored during OAuth flow)
  const storedToken = localStorage.getItem('apsToken');
  if (storedToken) {
    return storedToken;
  }

  // Try to get from sessionStorage
  const sessionToken = sessionStorage.getItem('apsToken');
  if (sessionToken) {
    return sessionToken;
  }

  // Try to get from auth state (if available)
  const authState = localStorage.getItem('authState');
  if (authState) {
    try {
      const parsed = JSON.parse(authState);
      return parsed.authToken || parsed.token;
    } catch {
      // Ignore parsing errors
    }
  }

  return null;
}

/**
 * Make API request with APS token in headers
 */
export async function apiRequestWithToken<T = any>(
  url: string, 
  options: RequestInit = {}
): Promise<T> {
  const token = getApsToken();
  
  if (!token) {
    throw new Error('No APS token available');
  }

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    'X-ACC-Token': token,
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // Still include cookies for session fallback
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `HTTP ${response.status}`;
    
    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.error || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    
    throw new Error(errorMessage);
  }

  return response.json();
}

/**
 * Get APS token for Viewer.js
 */
export async function getApsTokenForViewer(): Promise<{ access_token: string; expires_in: number }> {
  const token = getApsToken();
  
  if (!token) {
    throw new Error('No APS token available for Viewer');
  }

  // Return token in format expected by Autodesk Viewer
  return {
    access_token: token,
    expires_in: 3600 // 1 hour
  };
}

/**
 * Store APS token (called during OAuth flow)
 */
export function storeApsToken(token: string): void {
  localStorage.setItem('apsToken', token);
  sessionStorage.setItem('apsToken', token);
}

/**
 * Clear stored APS token
 */
export function clearApsToken(): void {
  localStorage.removeItem('apsToken');
  sessionStorage.removeItem('apsToken');
}

/**
 * Check if APS token is available
 */
export function hasApsToken(): boolean {
  return getApsToken() !== null;
}
